import React, { ReactNode, Ref, useRef, useState } from 'react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@curtin-dxp/curtin-ui';
import { EllipsisVertical } from 'lucide-react';
import { useNavigate } from 'react-router';
import { cn } from '@/lib/utils';

interface ActionDropdownOpenProps {
  options: ({ name: string; url: string } | ReactNode)[];
  actionDropdownRowId: string | null;
  setActionDropdownRowId: (id: string | null) => void;
  rowId: string;
  parentRef?: Ref<HTMLDivElement>;
  className?: string;
}

export const ActionsDropDown = ({
  options,
  actionDropdownRowId,
  setActionDropdownRowId,
  rowId,
  parentRef,
  className,
}: ActionDropdownOpenProps) => {
  const [open, setOpen] = useState<boolean>(false);
  const navigate = useNavigate();
  const selfRef = useRef<HTMLButtonElement | null>(null);

  const handleClick = (url: string): void => {
    navigate(url);
  };

  const nestedInteractOutside = (event: CustomEvent<{ originalEvent: MouseEvent | FocusEvent }>): void => {
    // find out if dropdown is open
    if (open) {
      event.preventDefault();
    }

    if (!parentRef) {
      setOpen(false);
      return;
    }

    if ('current' in parentRef && parentRef.current && selfRef.current) {
      const parentBoundingRect = parentRef.current.getBoundingClientRect();
      const selfBoundingRect = selfRef.current.getBoundingClientRect();
      if (
        event.type == 'dismissableLayer.focusOutside' &&
        event.detail.originalEvent.relatedTarget !== selfRef.current
      ) {
        return;
      }
      if (!parentBoundingRect || !selfBoundingRect) return;
      const {
        left: parentLeftBound,
        top: parentTopBound,
        right: parentRightBound,
        bottom: parentBottomBound,
      } = parentBoundingRect;
      const {
        left: selfLeftBound,
        top: selfTopBound,
        right: selfRightBound,
        bottom: selfBottomBound,
      } = selfBoundingRect;
      const originalEvent = event.detail.originalEvent as MouseEvent;
      const { clientX, clientY } = originalEvent;
      if (
        // Parent container clicked
        clientX <= parentRightBound &&
        clientX >= parentLeftBound &&
        clientY <= parentBottomBound &&
        clientY >= parentTopBound
      ) {
        if (
          // Button is clicked
          clientX <= selfRightBound &&
          clientX >= selfLeftBound &&
          clientY <= selfBottomBound &&
          clientY >= selfTopBound
        ) {
          // Do nothing
          return;
        }
      }
      setOpen(false);
    }
  };
  return (
    // <DropdownMenu open={open}>
    <DropdownMenu
      open={actionDropdownRowId === rowId}
      onOpenChange={(open) => {
        setActionDropdownRowId(open ? rowId : null);
      }}
    >
      <DropdownMenuTrigger asChild className="focus-visible:outline-none">
        <Button
          ref={selfRef}
          variant="muted"
          className={cn(
            `h-8 w-8 border-0 bg-transparent p-0 hover:bg-transparent focus:outline-none focus-visible:ring-1
            focus-visible:ring-inset focus-visible:ring-deep-blue focus-visible:ring-offset-0 ${open ? 'ring-1 ring-deep-blue' : ''}`,
            className,
          )}
          aria-label="Actions"
          aria-haspopup="menu"
          onClick={() => {
            setActionDropdownRowId(actionDropdownRowId === rowId ? null : rowId);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              setOpen(!open);
            }
          }}
        >
          <span className="sr-only">Open menu</span>
          <EllipsisVertical className="h-5 w-5 text-gray-400 hover:text-deep-blue" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        className="z-[100] rounded-2xl border-none bg-white shadow-xl"
        onInteractOutside={nestedInteractOutside}
        onEscapeKeyDown={() => setOpen(false)}
      >
        {options.map((option, index) => (
          <DropdownMenuItem
            key={index}
            className={
              'cursor-pointer py-[10px] font-semibold hover:text-deep-blue focus:text-deep-blue has-[button]:p-0'
            }
            onClick={() => {
              if (!React.isValidElement(option) && option && typeof option === 'object' && 'url' in option)
                handleClick(option.url);
              setOpen(false);
            }}
          >
            {!React.isValidElement(option)
              ? option && typeof option === 'object' && 'url' in option
                ? option.name
                : ''
              : option}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
